import { useMemo, useState } from 'react';
import { fmtCompact, fmtInt } from '../../utils/statsFormat';
import { axisGutter, niceTicks, seriesStyle, useChartTooltip, useElementWidth } from './useChartLayout';
import ChartTooltip from './ChartTooltip';
import './charts.css';

/**
 * Bar chart — single, grouped or stacked.
 *
 * Replaces MiniBarChart (Statistics), ActivityChart (History), and the
 * distance/channel histograms, which were three implementations of this with
 * three different CSS class systems.
 *
 * Props:
 *   data      [{ label, values: number[] }]  — one entry per category
 *   series    [{ name, slot? }]              — one entry per value index
 *   mode      'single' | 'grouped' | 'stacked'
 *   height    plot height in CSS px (the SVG renders at true pixel size)
 *   format    value formatter for tooltip + direct labels
 *   maxLabels how many x labels to show before thinning
 */
function BarChart({
  data = [],
  series = [{ name: '' }],
  mode = 'single',
  height = 240,
  format = fmtInt,
  tickFormat = fmtCompact,
  maxLabels = 8,
  labelExtreme = true,
}) {
  const [wrapRef, width] = useElementWidth(560);
  const { tip, show, hide } = useChartTooltip();
  const [hoverIdx, setHoverIdx] = useState(null);

  const stacked = mode === 'stacked';
  const grouped = mode === 'grouped';

  const { ticks, gutter, max } = useMemo(() => {
    let peak = 0;
    for (const row of data) {
      const vals = row.values || [];
      peak = stacked
        ? Math.max(peak, vals.reduce((a, b) => a + (b || 0), 0))
        : Math.max(peak, ...vals.map(v => v || 0));
    }
    const t = niceTicks(peak, 4);
    return { ticks: t, gutter: axisGutter(t, tickFormat), max: t[t.length - 1] || 1 };
  }, [data, stacked, tickFormat]);

  const pad = { top: 12, right: 12, bottom: 28, left: gutter };
  const plotW = Math.max(10, width - pad.left - pad.right);
  const plotH = Math.max(10, height - pad.top - pad.bottom);

  const n = data.length || 1;
  const slotW = plotW / n;
  const yOf = v => pad.top + plotH - ((v || 0) / max) * plotH;

  // 2px of surface between adjacent bars instead of a drawn border.
  const GAP = 2;
  const bandW = Math.max(1, Math.min(slotW - GAP, 56));
  const seriesCount = series.length || 1;
  const barW = grouped ? Math.max(1, (bandW - GAP * (seriesCount - 1)) / seriesCount) : bandW;

  const labelStep = Math.ceil(n / maxLabels);

  // Direct-label the extreme only. A number on every bar goes unread.
  const extremeIdx = useMemo(() => {
    if (!labelExtreme || n < 3) return -1;
    let best = -1, bestV = -Infinity;
    data.forEach((row, i) => {
      const v = stacked
        ? (row.values || []).reduce((a, b) => a + (b || 0), 0)
        : Math.max(...(row.values || [0]).map(x => x || 0));
      if (v > bestV) { bestV = v; best = i; }
    });
    return bestV > 0 ? best : -1;
  }, [data, stacked, labelExtreme, n]);

  const rowsFor = (row) => series.map((s, si) => ({
    name: s.name,
    value: format((row.values || [])[si]),
    color: seriesStyle(s.slot != null ? s.slot : si + 1).color,
  }));

  const enter = (i, evt) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    setHoverIdx(i);
    show({
      x: pad.left + i * slotW + slotW / 2,
      y: rect ? Math.min(plotH, Math.max(0, evt.clientY - rect.top)) : plotH / 2,
      title: data[i].label,
      rows: rowsFor(data[i]),
    });
  };

  const leave = () => { setHoverIdx(null); hide(); };

  return (
    <div className="chart-plot" ref={wrapRef}>
      <svg className="chart-svg" width="100%" height={height}
           viewBox={`0 0 ${width} ${height}`} role="img">
        {/* Gridlines: solid hairlines one shade off the surface. Never dashed —
            dashing reads as "threshold" when it is only a grid. */}
        {ticks.map((t, i) => (
          <g key={`t${i}`}>
            <line x1={pad.left} y1={yOf(t)} x2={width - pad.right} y2={yOf(t)}
                  className="chart-grid-line" />
            <text x={pad.left - 7} y={yOf(t) + 4} className="chart-axis-label"
                  textAnchor="end">{tickFormat(t)}</text>
          </g>
        ))}

        {data.map((row, i) => {
          const slotX = pad.left + i * slotW;
          const bandX = slotX + (slotW - bandW) / 2;
          let stackTop = 0;

          return (
            <g key={`c${i}`} className={hoverIdx === i ? 'chart-band is-hover' : 'chart-band'}>
              {series.map((s, si) => {
                const v = (row.values || [])[si] || 0;
                const { color } = seriesStyle(s.slot != null ? s.slot : si + 1);
                let x, y, h;
                if (stacked) {
                  const base = yOf(stackTop);
                  stackTop += v;
                  y = yOf(stackTop);
                  h = Math.max(0, base - y - (si > 0 ? GAP : 0));
                  x = bandX;
                } else if (grouped) {
                  x = bandX + si * (barW + GAP);
                  y = yOf(v);
                  h = Math.max(0, pad.top + plotH - y);
                } else {
                  x = bandX;
                  y = yOf(v);
                  h = Math.max(0, pad.top + plotH - y);
                }
                if (h <= 0) return null;
                return (
                  <rect key={`b${si}`} x={x} y={y} width={grouped ? barW : bandW} height={h}
                        rx="3" fill={color} className="chart-bar" />
                );
              })}

              {/* The hit target is the whole column slot, not the painted bar:
                  a 3px bar is otherwise impossible to hit reliably. */}
              <rect
                x={slotX} y={pad.top} width={slotW} height={plotH}
                className="chart-hit"
                tabIndex={0}
                role="button"
                aria-label={`${row.label}: ${series.map((s, si) =>
                  `${s.name ? s.name + ' ' : ''}${format((row.values || [])[si])}`).join(', ')}`}
                onPointerMove={(e) => enter(i, e)}
                onPointerEnter={(e) => enter(i, e)}
                onPointerLeave={leave}
                onFocus={(e) => enter(i, e)}
                onBlur={leave}
              />

              {i === extremeIdx && (
                <text x={slotX + slotW / 2}
                      y={Math.max(pad.top + 10, yOf(stacked
                        ? (row.values || []).reduce((a, b) => a + (b || 0), 0)
                        : Math.max(...(row.values || [0]).map(x => x || 0))) - 6)}
                      className="chart-value-label" textAnchor="middle">
                  {format(stacked
                    ? (row.values || []).reduce((a, b) => a + (b || 0), 0)
                    : Math.max(...(row.values || [0]).map(x => x || 0)))}
                </text>
              )}

              {i % labelStep === 0 && (
                <text x={slotX + slotW / 2} y={height - 9}
                      className="chart-axis-label" textAnchor="middle">{row.label}</text>
              )}
            </g>
          );
        })}
      </svg>

      <ChartTooltip tip={tip} containerWidth={width} />
    </div>
  );
}

export default BarChart;
